import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';
import SEOHead from '../../components/base/SEOHead';
import { generateWebPageSchema, generateBreadcrumbSchema } from '../../utils/seo';

const RecruitPage = () => {
  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://example.com';

  // Generate structured data
  const structuredData = [
    generateWebPageSchema(
      '求人情報｜おかやま放課後等デイサービス｜岡山市の療育スタッフ募集',
      '岡山市の放課後等デイサービス「おかやま放課後等デイサービス」の求人情報。児童発達支援管理責任者、児童指導員、作業療法士を募集中。やりがいのある職場で一緒に働きませんか。',
      '/recruit'
    ),
    generateBreadcrumbSchema([
      { name: 'ホーム', url: '/' },
      { name: '求人情報', url: '/recruit' }
    ])
  ];

  const positions = [
    {
      title: '児童発達支援管理責任者',
      type: '正社員',
      salary: '月給 280,000円〜350,000円',
      requirements: [
        '児童発達支援管理責任者研修修了',
        '実務経験5年以上',
        '普通自動車免許（AT限定可）'
      ],
      description: '個別支援計画の作成・管理、スタッフの指導・育成を担当していただきます。',
      image: 'https://public.readdy.ai/ai/img_res/fcf8bb79e778d61503605f4b26edb207.jpg'
    },
    {
      title: '児童指導員',
      type: '正社員・パート',
      salary: '正社員: 月給 220,000円〜280,000円 / パート: 時給 1,200円〜1,500円',
      requirements: [
        '保育士・教員免許・社会福祉士等の資格',
        '児童福祉に関する経験（未経験者も歓迎）',
        '普通自動車免許（AT限定可）'
      ],
      description: 'お子様の日常生活支援、療育活動の実施、保護者との連携を行っていただきます。',
      image: 'https://readdy.ai/api/search-image?query=Japanese%20childcare%20worker%20with%20children%2C%20warm%20interaction%2C%20colorful%20therapy%20room%2C%20caring%20atmosphere%2C%20educational%20activities%2C%20natural%20lighting%2C%20professional%20setting&width=300&height=200&seq=job2&orientation=landscape'
    },
    {
      title: '作業療法士',
      type: '正社員・パート',
      salary: '正社員: 月給 250,000円〜320,000円 / パート: 時給 1,400円〜1,800円',
      requirements: [
        '作業療法士国家資格',
        '小児分野での経験（新卒者も歓迎）',
        '普通自動車免許（AT限定可）'
      ],
      description: '感覚統合療法、運動療法、日常生活動作訓練等の専門的な療育を提供していただきます。',
      image: 'https://readdy.ai/api/search-image?query=Japanese%20occupational%20therapist%20working%20with%20child%2C%20therapy%20equipment%2C%20bright%20therapy%20room%2C%20professional%20interaction%2C%20specialized%20tools%2C%20natural%20lighting%2C%20caring%20environment&width=300&height=200&seq=job3&orientation=landscape'
    }
  ];

  const benefits = [
    {
      icon: 'ri-money-dollar-circle-fill',
      title: '充実の待遇',
      description: '昇給年1回、賞与年2回、各種手当充実',
      color: 'text-green-500'
    },
    {
      icon: 'ri-time-fill',
      title: '働きやすい環境',
      description: '残業少なめ、有給取得率90%以上',
      color: 'text-blue-500'
    },
    {
      icon: 'ri-graduation-cap-fill',
      title: '研修制度',
      description: '外部研修参加支援、資格取得支援制度',
      color: 'text-purple-500'
    },
    {
      icon: 'ri-team-fill',
      title: 'チームワーク',
      description: 'アットホームな職場、チームワーク重視',
      color: 'text-orange-500'
    }
  ];

  const workEnvironment = [
    '年間休日120日以上',
    '有給休暇取得率90%以上',
    '残業月平均5時間以下',
    '育児休業・介護休業制度',
    '退職金制度（勤続3年以上）',
    '健康診断・ストレスチェック',
    '職員駐車場完備',
    '制服貸与'
  ];

  return (
    <div className="min-h-screen bg-white">
      <SEOHead
        title="求人情報｜おかやま放課後等デイサービス｜岡山市の療育スタッフ募集"
        description="岡山市の放課後等デイサービス「おかやま放課後等デイサービス」の求人情報。児童発達支援管理責任者、児童指導員、作業療法士を募集中。やりがいのある職場で一緒に働きませんか。"
        keywords="岡山市,放課後等デイサービス,求人,募集,児童指導員,作業療法士,療育スタッフ"
        canonicalUrl={`${siteUrl}/recruit`}
        structuredData={structuredData}
      />
      
      <Header />
      
      {/* Hero Section */}
      <section className="relative py-20 md:py-24 bg-gradient-to-br from-purple-600 to-blue-600">
        <div className="absolute inset-0 bg-black bg-opacity-20"></div>
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-4xl sm:text-5xl md:text-6xl font-bold text-white mb-6 md:mb-8">
            <strong>求人情報</strong>
          </h1>
          <p className="text-lg sm:text-xl md:text-2xl text-white max-w-3xl mx-auto leading-relaxed">
            子どもたちの成長を一緒にサポートしてくださる仲間を募集しています。やりがいのある職場で、あなたの力を活かしませんか？
          </p>
        </div>
      </section>

      {/* Job Positions */}
      <section className="py-16 md:py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12 md:mb-16">
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-gray-800 mb-4 md:mb-6">
              <strong>募集職種</strong>
            </h2>
            <p className="text-base sm:text-lg md:text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
              様々な職種で専門性を活かして働ける環境をご用意しています。
            </p>
          </div>
          
          <div className="space-y-8 md:space-y-10">
            {positions.map((position, index) => (
              <div key={index} className="bg-gradient-to-br from-blue-50 to-purple-50 rounded-xl shadow-xl overflow-hidden hover:shadow-2xl transition-all duration-300">
                <div className="md:flex">
                  <div className="md:w-1/3">
                    <img
                      src={position.image}
                      alt={position.title}
                      className="w-full h-56 sm:h-64 md:h-full object-cover"
                    />
                  </div>
                  <div className="md:w-2/3 p-6 md:p-10">
                    <div className="flex flex-wrap items-center gap-3 mb-4 md:mb-6">
                      <h3 className="text-2xl sm:text-3xl md:text-4xl font-bold text-gray-800 break-words">{position.title}</h3>
                      <span className="px-3 py-1 bg-purple-100 text-purple-800 rounded-full text-sm md:text-base font-semibold whitespace-nowrap shadow-sm">
                        {position.type}
                      </span>
                    </div>
                    
                    <div className="mb-4 md:mb-6">
                      <h4 className="text-lg sm:text-xl md:text-2xl font-semibold text-gray-800 mb-3">給与</h4>
                      <p className="text-base sm:text-lg md:text-xl text-gray-600 break-words">{position.salary}</p>
                    </div>
                    
                    <div className="mb-4 md:mb-6">
                      <h4 className="text-lg sm:text-xl md:text-2xl font-semibold text-gray-800 mb-3">応募資格</h4>
                      <ul className="space-y-2">
                        {position.requirements.map((req, reqIndex) => (
                          <li key={reqIndex} className="flex items-start gap-3">
                            <i className="ri-check-line text-green-500 text-lg md:text-xl mt-1 flex-shrink-0"></i>
                            <span className="text-sm sm:text-base md:text-lg text-gray-600 break-words">{req}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                    
                    <div className="mb-6 md:mb-8">
                      <h4 className="text-lg sm:text-xl md:text-2xl font-semibold text-gray-800 mb-3">仕事内容</h4>
                      <p className="text-base sm:text-lg md:text-xl text-gray-600 break-words">{position.description}</p>
                    </div>
                    
                    <button className="bg-purple-600 text-white px-6 md:px-8 py-3 md:py-4 text-base md:text-lg rounded-lg hover:bg-purple-700 transition-all duration-300 cursor-pointer whitespace-nowrap shadow-lg hover:shadow-xl">
                      <i className="ri-mail-fill mr-2"></i>
                      この職種に応募する
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section className="py-16 md:py-20 bg-gradient-to-br from-purple-50 to-pink-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12 md:mb-16">
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-gray-800 mb-4 md:mb-6">
              <strong>働く魅力</strong>
            </h2>
            <p className="text-base sm:text-lg md:text-xl text-gray-600">
              スタッフが長く安心して働ける環境づくりに取り組んでいます。
            </p>
          </div>
          
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 md:gap-8">
            {benefits.map((benefit, index) => (
              <div key={index} className="bg-white rounded-xl shadow-lg p-6 md:p-8 text-center hover:shadow-2xl hover:-translate-y-2 transition-all duration-300">
                <div className={`w-16 h-16 md:w-20 md:h-20 ${benefit.color} bg-opacity-10 rounded-full flex items-center justify-center mx-auto mb-4 md:mb-6`}>
                  <i className={`${benefit.icon} text-2xl md:text-3xl ${benefit.color}`}></i>
                </div>
                <h3 className="text-lg sm:text-xl md:text-2xl font-semibold text-gray-800 mb-3 md:mb-4 break-words">{benefit.title}</h3>
                <p className="text-sm sm:text-base md:text-lg text-gray-600 leading-relaxed break-words">{benefit.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Work Environment */}
      <section className="py-16 md:py-20 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12 md:mb-16">
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-gray-800 mb-4 md:mb-6">
              <strong>福利厚生・労働条件</strong>
            </h2>
            <p className="text-base sm:text-lg md:text-xl text-gray-600">
              スタッフが安心して働ける充実した制度をご用意しています。
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6">
            {workEnvironment.map((item, index) => (
              <div key={index} className="bg-gradient-to-r from-purple-50 to-pink-50 rounded-lg shadow-lg p-5 md:p-6 flex items-center gap-4 hover:shadow-xl transition-all duration-300 hover:-translate-y-1">
                <i className="ri-check-line text-purple-500 text-2xl md:text-3xl flex-shrink-0"></i>
                <span className="text-base sm:text-lg md:text-xl text-gray-700 break-words">{item}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Application Process */}
      <section className="py-16 md:py-20 bg-gradient-to-br from-purple-50 to-pink-50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12 md:mb-16">
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-gray-800 mb-4 md:mb-6">
              <strong>応募の流れ</strong>
            </h2>
          </div>
          
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 md:gap-8">
            <div className="text-center hover:-translate-y-2 transition-all duration-300">
              <div className="w-16 h-16 md:w-20 md:h-20 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-4 md:mb-6 shadow-md">
                <span className="text-2xl md:text-3xl font-bold text-purple-600">1</span>
              </div>
              <h3 className="text-lg sm:text-xl md:text-2xl font-semibold text-gray-800 mb-3">お問い合わせ</h3>
              <p className="text-sm sm:text-base md:text-lg text-gray-600 break-words">お電話またはメールでお気軽にお問い合わせください</p>
            </div>
            
            <div className="text-center hover:-translate-y-2 transition-all duration-300">
              <div className="w-16 h-16 md:w-20 md:h-20 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-4 md:mb-6 shadow-md">
                <span className="text-2xl md:text-3xl font-bold text-purple-600">2</span>
              </div>
              <h3 className="text-lg sm:text-xl md:text-2xl font-semibold text-gray-800 mb-3">書類選考</h3>
              <p className="text-sm sm:text-base md:text-lg text-gray-600 break-words">履歴書・職務経歴書による書類選考を行います</p>
            </div>
            
            <div className="text-center hover:-translate-y-2 transition-all duration-300">
              <div className="w-16 h-16 md:w-20 md:h-20 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-4 md:mb-6 shadow-md">
                <span className="text-2xl md:text-3xl font-bold text-purple-600">3</span>
              </div>
              <h3 className="text-lg sm:text-xl md:text-2xl font-semibold text-gray-800 mb-3">面接</h3>
              <p className="text-sm sm:text-base md:text-lg text-gray-600 break-words">施設見学を兼ねた面接を実施いたします</p>
            </div>
            
            <div className="text-center hover:-translate-y-2 transition-all duration-300">
              <div className="w-16 h-16 md:w-20 md:h-20 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-4 md:mb-6 shadow-md">
                <span className="text-2xl md:text-3xl font-bold text-purple-600">4</span>
              </div>
              <h3 className="text-lg sm:text-xl md:text-2xl font-semibold text-gray-800 mb-3">内定・入社</h3>
              <p className="text-sm sm:text-base md:text-lg text-gray-600 break-words">内定後、入社日を調整し研修を開始します</p>
            </div>
          </div>
          
          <div className="mt-12 md:mt-16 text-center">
            <div className="bg-white rounded-xl shadow-xl p-8 md:p-10">
              <h3 className="text-xl sm:text-2xl md:text-3xl font-semibold text-gray-800 mb-4 md:mb-6">お問い合わせ・応募</h3>
              <p className="text-base sm:text-lg md:text-xl text-gray-600 mb-6 md:mb-8">
                ご質問やご応募は、お電話またはメールでお気軽にお問い合わせください。
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <a
                  href="tel:086-012-3456"
                  className="inline-flex items-center justify-center px-8 md:px-10 py-4 bg-green-600 text-white text-base md:text-lg font-semibold rounded-lg hover:bg-green-700 transition-all duration-300 cursor-pointer whitespace-nowrap shadow-lg hover:shadow-xl"
                >
                  <i className="ri-phone-fill mr-2"></i>
                  086-012-3456
                </a>
                <a
                  href="#contact-form"
                  className="inline-flex items-center justify-center px-8 md:px-10 py-4 bg-purple-600 text-white text-base md:text-lg font-semibold rounded-lg hover:bg-purple-700 transition-all duration-300 cursor-pointer whitespace-nowrap shadow-lg hover:shadow-xl"
                >
                  <i className="ri-mail-fill mr-2"></i>
                  メールで応募
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default RecruitPage;
